import { useTheme } from '../contexts/ThemeContext';
import { useEffect, useState } from 'react';

// Floating particles for alien/space effect
const FloatingParticle = ({ delay, duration, size, color, startX, startY }) => (
  <div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size,
      height: size,
      background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
      left: `${startX}%`,
      top: `${startY}%`,
      animation: `stardustFloat ${duration}s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      filter: `blur(${size > 10 ? 2 : 0}px)`,
      boxShadow: `0 0 ${size * 2}px ${color}`
    }}
  />
);

// UFO component
const UFO = ({ size, x, y, delay, reverse }) => (
  <div
    className="absolute pointer-events-none select-none"
    style={{
      fontSize: size,
      left: `${x}%`,
      top: `${y}%`,
      animation: `ufoFloat ${8 + delay}s ease-in-out infinite ${reverse ? 'reverse' : ''}`,
      animationDelay: `${delay}s`,
      filter: 'drop-shadow(0 0 20px rgba(0, 255, 102, 0.6))',
      zIndex: 2
    }}
  >
    🛸
  </div>
);

// Alien component
const Alien = ({ x, y, delay }) => (
  <div
    className="absolute pointer-events-none select-none"
    style={{
      fontSize: '2rem',
      left: `${x}%`,
      top: `${y}%`,
      animation: `ufoFloat ${12}s ease-in-out infinite reverse`,
      animationDelay: `${delay}s`,
      filter: 'drop-shadow(0 0 15px rgba(0, 255, 102, 0.5))',
      zIndex: 2
    }}
  >
    👽
  </div>
);

// Radar sweep for UFO Hunter theme
const RadarSweep = ({ color }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    <div
      className="absolute"
      style={{
        top: '50%',
        left: '50%',
        width: '150%',
        height: '150%',
        background: `conic-gradient(from 0deg, transparent 0deg, ${color}40 30deg, transparent 60deg)`,
        transform: 'translate(-50%, -50%)',
        animation: 'radarSweep 4s linear infinite',
        opacity: 0.3
      }}
    />
    {/* Radar grid */}
    <div
      className="absolute inset-0"
      style={{
        background: `
          linear-gradient(${color}15 1px, transparent 1px),
          linear-gradient(90deg, ${color}15 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
        opacity: 0.5
      }}
    />
    {/* Radar circles */}
    {[100, 200, 300].map((size, i) => (
      <div
        key={i}
        className="absolute rounded-full"
        style={{
          top: '50%',
          left: '50%',
          width: size,
          height: size,
          border: `1px solid ${color}30`,
          transform: 'translate(-50%, -50%)'
        }}
      />
    ))}
  </div>
);

// Tentacle effect for Cosmic Horror
const Tentacle = ({ delay, x }) => (
  <div
    className="absolute bottom-0 pointer-events-none"
    style={{
      left: `${x}%`,
      width: '4px',
      height: '30%',
      background: `linear-gradient(to top, #8b00ff80, transparent)`,
      borderRadius: '2px',
      animation: `tentacleWave ${3 + delay}s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      transformOrigin: 'bottom center',
      filter: 'blur(1px)'
    }}
  />
);

// Blood drip for Void Walker
const BloodDrip = ({ x, delay }) => (
  <div
    className="absolute top-0 pointer-events-none"
    style={{
      left: `${x}%`,
      width: '3px',
      height: '0',
      background: 'linear-gradient(to bottom, #ff0033, #990000)',
      borderRadius: '0 0 3px 3px',
      animation: `bloodDrip ${2 + delay}s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      boxShadow: '0 0 10px #ff003380'
    }}
  />
);

// Alien beam
const AlienBeam = ({ x }) => (
  <div
    className="absolute pointer-events-none"
    style={{
      left: `${x}%`,
      top: '5%',
      width: '60px',
      height: '0',
      background: 'linear-gradient(to bottom, #00ff6680 0%, transparent 100%)',
      clipPath: 'polygon(30% 0%, 70% 0%, 100% 100%, 0% 100%)',
      animation: 'alienBeam 5s ease-in-out infinite',
      filter: 'blur(2px)',
      transform: 'translateX(-50%)'
    }}
  />
);

// Main animated background component
const AnimatedBackground = () => {
  const { theme, currentTheme } = useTheme();
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Generate random particles based on theme
    const newParticles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      delay: Math.random() * 5,
      duration: 5 + Math.random() * 10,
      size: 4 + Math.random() * 12,
      color: theme.colors.primary,
      startX: Math.random() * 100,
      startY: Math.random() * 100
    }));
    setParticles(newParticles);
  }, [currentTheme, theme.colors.primary]);

  const renderThemeEffects = () => {
    switch (currentTheme) {
      case 'cosmicHorror':
        return (
          <>
            {/* Eldritch tentacles */}
            {[10, 25, 40, 55, 70, 85].map((x, i) => (
              <Tentacle key={i} x={x} delay={i * 0.5} />
            ))}
            {/* Pulsing void circles */}
            <div 
              className="absolute rounded-full"
              style={{
                top: '20%',
                left: '15%',
                width: '200px',
                height: '200px',
                background: `radial-gradient(circle, ${theme.colors.primary}20 0%, transparent 70%)`,
                animation: 'eldritchPulse 8s ease-in-out infinite',
                filter: 'blur(20px)'
              }}
            />
            <div 
              className="absolute rounded-full"
              style={{
                bottom: '10%',
                right: '20%',
                width: '300px',
                height: '300px',
                background: `radial-gradient(circle, ${theme.colors.secondary}15 0%, transparent 70%)`,
                animation: 'eldritchPulse 10s ease-in-out infinite reverse',
                filter: 'blur(30px)'
              }}
            />
          </>
        );

      case 'alienEncounter':
        return (
          <>
            {/* UFOs */}
            <UFO size="3rem" x={10} y={15} delay={0} />
            <UFO size="2rem" x={75} y={25} delay={2} reverse />
            <UFO size="2.5rem" x={50} y={60} delay={4} />
            {/* Aliens */}
            <Alien x={85} y={70} delay={1} />
            <Alien x={5} y={45} delay={3} />
            {/* Alien beams */}
            <AlienBeam x={30} />
            <AlienBeam x={70} />
            {/* Green glow areas */}
            <div 
              className="absolute"
              style={{
                top: '30%',
                left: '25%',
                width: '200px',
                height: '200px',
                background: `radial-gradient(circle, ${theme.colors.primary}15 0%, transparent 60%)`,
                animation: 'bioGlow 4s ease-in-out infinite',
                filter: 'blur(40px)'
              }}
            />
          </>
        );

      case 'ufoHunter':
        return (
          <>
            <RadarSweep color={theme.colors.primary} />
            {/* Scan lines */}
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `repeating-linear-gradient(
                  0deg,
                  transparent,
                  transparent 2px,
                  ${theme.colors.primary}08 2px,
                  ${theme.colors.primary}08 4px
                )`,
                animation: 'scanLine 8s linear infinite'
              }}
            />
            {/* UFO blips */}
            {[
              { x: 30, y: 40 },
              { x: 65, y: 25 },
              { x: 45, y: 70 }
            ].map((pos, i) => (
              <div
                key={i}
                className="absolute rounded-full"
                style={{
                  left: `${pos.x}%`,
                  top: `${pos.y}%`,
                  width: '8px',
                  height: '8px',
                  background: theme.colors.primary,
                  boxShadow: `0 0 10px ${theme.colors.primary}`,
                  animation: `radarPing 2s ease-out infinite`,
                  animationDelay: `${i * 0.7}s`
                }}
              />
            ))}
          </>
        );

      case 'voidWalker':
        return (
          <>
            {/* Blood drips */}
            {[15, 35, 50, 65, 85].map((x, i) => (
              <BloodDrip key={i} x={x} delay={i * 0.8} />
            ))}
            {/* Pulsing void */}
            <div 
              className="absolute inset-0"
              style={{
                background: `radial-gradient(circle at 50% 50%, transparent 0%, ${theme.colors.void}ee 100%)`,
                animation: 'voidPulse 4s ease-in-out infinite'
              }}
            />
            {/* Heartbeat glow */}
            <div 
              className="absolute rounded-full"
              style={{
                top: '50%',
                left: '50%',
                width: '100px',
                height: '100px',
                transform: 'translate(-50%, -50%)',
                background: `radial-gradient(circle, ${theme.colors.primary}30 0%, transparent 70%)`,
                animation: 'heartbeat 1.5s ease-in-out infinite',
                filter: 'blur(20px)'
              }}
            />
          </>
        );

      case 'deepNebula':
        return (
          <>
            {/* Swirling nebula effect */}
            <div 
              className="absolute"
              style={{
                top: '10%',
                right: '10%',
                width: '400px',
                height: '400px',
                background: `conic-gradient(from 0deg, ${theme.colors.primary}10, ${theme.colors.secondary}10, ${theme.colors.primary}10)`,
                borderRadius: '50%',
                animation: 'nebulaSwirl 30s linear infinite',
                filter: 'blur(60px)'
              }}
            />
            <div 
              className="absolute"
              style={{
                bottom: '20%',
                left: '5%',
                width: '300px',
                height: '300px',
                background: `conic-gradient(from 180deg, ${theme.colors.secondary}15, ${theme.colors.primary}15, ${theme.colors.secondary}15)`,
                borderRadius: '50%',
                animation: 'nebulaSwirl 25s linear infinite reverse',
                filter: 'blur(50px)'
              }}
            />
          </>
        );

      default: // Original theme
        return (
          <>
            {/* Subtle scanning effect */}
            <div 
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `linear-gradient(180deg, transparent 0%, ${theme.colors.primary}05 50%, transparent 100%)`,
                animation: 'scanLine 6s ease-in-out infinite'
              }}
            />
          </>
        );
    }
  };

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {/* Floating particles */}
      {particles.map(particle => (
        <FloatingParticle key={particle.id} {...particle} />
      ))}
      
      {/* Theme-specific effects */}
      {renderThemeEffects()}
      
      {/* Global ambient glow */}
      <div 
        className="absolute"
        style={{
          top: '20%',
          left: '50%',
          width: '600px',
          height: '600px',
          transform: 'translateX(-50%)',
          background: `radial-gradient(circle, ${theme.colors.primary}08 0%, transparent 50%)`,
          filter: 'blur(80px)',
          pointerEvents: 'none'
        }}
      />
    </div>
  );
};

export default AnimatedBackground;
